import React, { useState, useEffect } from 'react';
import './ChalisaList.css';
import AddChalisaForm from './AddChalisaForm';
import apiClient from '../../api';

const ChalisaList = () => {
  const [chalisas, setChalisas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingChalisa, setEditingChalisa] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', description: '', content: '' });

  useEffect(() => {
    fetchChalisas();
  }, []);

  const fetchChalisas = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/chalisa/list/');
      const data = response.data;
      if (Array.isArray(data)) {
        setChalisas(data);
      } else if (data && Array.isArray(data.results)) {
        setChalisas(data.results);
      } else {
        setChalisas([]);
      }
      setError(null);
    } catch (error) {
      console.error('Error fetching chalisas:', error);
      setError('Failed to load chalisas');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (chalisaId) => {
    if (!window.confirm('Are you sure you want to delete this chalisa?')) return;

    try {
      await apiClient.delete(`/api/chalisa/delete/${chalisaId}/`);
      alert('Chalisa deleted successfully');
      await fetchChalisas();
    } catch (error) {
      console.error('Error deleting chalisa:', error);
      alert('Error deleting chalisa');
    }
  };

  const handleSave = async (formData) => {
    try {
      await apiClient.post('/api/chalisa/add/', formData);
      setShowAddForm(false);
      alert('Chalisa created successfully');
      await fetchChalisas();
    } catch (error) {
      console.error('Error creating chalisa:', error);
      alert('Error creating chalisa');
    }
  };

  const handleEditClick = (chalisa) => {
    setEditingChalisa(chalisa);
    setEditFormData({
      name: chalisa.name || '',
      description: chalisa.description || '',
      content: chalisa.content || ''
    });
  };

  const handleEditCancel = () => {
    setEditingChalisa(null);
    setEditFormData({ name: '', description: '', content: '' });
  };

  const handleEditSave = async () => {
    try {
      await apiClient.put(`/api/chalisa/update/${editingChalisa.id}/`, editFormData);
      alert('Chalisa updated successfully');
      setEditingChalisa(null);
      setEditFormData({ name: '', description: '', content: '' });
      await fetchChalisas();
    } catch (error) {
      console.error('Error updating chalisa:', error);
      alert('Error updating chalisa');
    }
  };

  const filteredChalisas = chalisas.filter(chalisa =>
    chalisa.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (chalisa.description && chalisa.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (showAddForm) {
    return (
      <AddChalisaForm
        onClose={() => setShowAddForm(false)}
        onSave={handleSave}
      />
    );
  }

  if (loading) return <div className="loading-message">Loading chalisas...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="chalisa-list">
      {/* Edit Modal */}
      {editingChalisa && (
        <div className="edit-modal-overlay">
          <div className="edit-modal">
            <h2>✏️ Edit Chalisa</h2>
            <div className="edit-form">
              <label>Name:</label>
              <input
                type="text"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              />
              <label>Description:</label>
              <textarea
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                rows={3}
              />
              <label>Content:</label>
              <textarea
                value={editFormData.content}
                onChange={(e) => setEditFormData({ ...editFormData, content: e.target.value })}
                rows={8}
              />
              <div className="edit-modal-buttons">
                <button className="save-button" onClick={handleEditSave}>💾 Save</button>
                <button className="cancel-button" onClick={handleEditCancel}>❌ Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="chalisa-header">
        <h1 className="chalisa-title">📖 Chalisa Management</h1>
        <button onClick={() => setShowAddForm(true)} className="add-chalisa-button">
          ➕ Add New Chalisa
        </button>
      </div>

      <div className="search-bar-container">
        <input
          type="text"
          placeholder="🔍 Search chalisas by name or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredChalisas.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📖</div>
          <h3 className="empty-title">
            {searchTerm ? 'No Matching Chalisas Found' : 'No Chalisas Found'}
          </h3>
          <p className="empty-description">
            {searchTerm
              ? 'Try adjusting your search term or clear the search to see all chalisas.'
              : 'Start building your chalisa collection by adding your first chalisa.'
            }
          </p>
          {!searchTerm && (
            <button onClick={() => setShowAddForm(true)} className="add-chalisa-button">
              ➕ Add Your First Chalisa
            </button>
          )}
        </div>
      ) : (
        <div className="chalisa-grid">
          {filteredChalisas.map((chalisa) => (
            <div key={chalisa.id} className="chalisa-card">
              <div className="chalisa-info">
                <h3 className="chalisa-name">{chalisa.name}</h3>
                {chalisa.description && (
                  <p className="chalisa-description">{chalisa.description}</p>
                )}
              </div>
              <div className="action-buttons">
                <button onClick={() => handleEditClick(chalisa)} className="edit-button">
                  ✏️ Edit
                </button>
                <button onClick={() => handleDelete(chalisa.id)} className="delete-button">
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChalisaList;